import React, { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import SiteFooter from "@/components/SiteFooter";
import { ArrowRight, BookOpen, Download, ExternalLink, FileText, Eye, FileDown } from "lucide-react";

// iter-62 — Public showcase gallery at /showcase. Each entry links to a
// before/after set: the raw manuscript the author uploaded and the
// BookForge360 rendered PDF + DOCX. First entry uses Richard Lee Garcia's
// "Your Nervous System Is Stuck" manuscript — a real 22-chapter medical
// book we compiled through iter-56 → iter-60 of the formatter_v2 pipeline.

const SHOWCASE_ENTRIES = [
  {
    id: "nervous-system",
    title: "Your Nervous System Is Stuck",
    subtitle:
      "How Chronic Stress Locks Your Body in Survival Mode — and the Daily Practices That Restore Safety and Healing",
    author: "Richard Lee Garcia, RN, BSN",
    tag: "Health · 22 chapters",
    highlights: [
      "44 chapters detected across 6 Parts",
      "36 RN Insight callouts + 22 Bedside Example callouts wrapped as styled asides",
      "768 unbulleted list items auto-promoted to <ul>",
      "69 ad-hoc H3 section titles reclassified out of body-text",
    ],
    before:
      "https://customer-assets-gfyr7b9c.emergentagent.net/job_book-format-pro/artifacts/o2wll6f2_Ebook%20103%20-%20Your%20Nervous%20System%20Is%20Stuck-original-manuscript.docx",
    afterPdf: "/reformatted/nervous-system-reformatted.pdf",
    afterDocx: "/reformatted/nervous-system-reformatted.docx",
    coverGradient:
      "linear-gradient(135deg, #f0ede5 0%, #e6d9c9 100%)",
    accent: "#D95D39",
  },
];

// Placeholder "coming soon" entries so the gallery doesn't look empty
// while we onboard more author showcases. Real books will replace these
// after the launch cohort ships.
const COMING_SOON = [
  {
    title: "The Founder's Cadence",
    author: "In production",
    tag: "Business · Workbook",
    accent: "#3f8f8f",
    coverGradient: "linear-gradient(135deg, #eaf1ee 0%, #cfe0d7 100%)",
  },
  {
    title: "Letters From A Long Field",
    author: "In production",
    tag: "Memoir · Prose",
    accent: "#5b6ab0",
    coverGradient: "linear-gradient(135deg, #ecebf4 0%, #cfd3ea 100%)",
  },
  {
    title: "The Everyday Astronomer",
    author: "In production",
    tag: "Non-fiction · Illustrated",
    accent: "#b4732c",
    coverGradient: "linear-gradient(135deg, #f4ecdb 0%, #dbc59a 100%)",
  },
];

export default function Showcase() {
  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <Nav />

      {/* ─── Header ─────────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-6 pt-16 pb-14">
        <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
          Showcase · Real books, real authors
        </p>
        <h1
          className="font-editorial font-light tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-8xl mt-4"
          data-testid="showcase-h1"
        >
          Manuscripts we've
          <span className="block italic text-[color:var(--primary)]">
            typeset in one pass.
          </span>
        </h1>
        <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[color:var(--text-muted)]">
          Every book below started as a raw Word document. Each finished as a
          print-ready PDF, a compliant EPUB, and a clean DOCX — with zero
          manual layout work. Download the before-and-after files to inspect
          the pipeline's output at page-turn resolution.
        </p>
      </section>

      {/* ─── Featured entry (full detail) ────────────────────────── */}
      {SHOWCASE_ENTRIES.map((book) => (
        <section
          key={book.id}
          className="border-t border-[color:var(--border)] bg-white"
          data-testid={`showcase-featured-${book.id}`}
        >
          <div className="max-w-[1400px] mx-auto px-6 py-20">
            <div className="grid grid-cols-12 gap-10 items-center">
              <div className="col-span-12 lg:col-span-5">
                <div
                  className="aspect-[3/4] rounded-lg shadow-[0_30px_60px_-30px_rgba(0,0,0,0.35)] p-8 flex flex-col justify-between"
                  style={{ background: book.coverGradient }}
                  data-testid={`showcase-cover-${book.id}`}
                >
                  <div
                    className="text-[10px] uppercase tracking-[0.3em]"
                    style={{ color: book.accent }}
                  >
                    BookForge360 · Compiled
                  </div>
                  <div>
                    <div
                      className="font-editorial text-3xl font-bold leading-tight text-[#1a1a1a]"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {book.title}
                    </div>
                    <div
                      className="italic mt-3 text-sm text-[#3a3a3a] leading-snug"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {book.subtitle}
                    </div>
                    <div className="mt-6 text-sm text-[#1a1a1a]">
                      {book.author}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-7">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)]">
                  {book.tag}
                </span>
                <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
                  {book.title}
                </h2>
                <p className="mt-4 italic text-lg text-[color:var(--text-muted)] leading-relaxed">
                  {book.subtitle}
                </p>
                <div className="mt-3 text-sm">by {book.author}</div>

                <ul className="mt-8 space-y-3">
                  {book.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm">
                      <span
                        className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                        style={{ background: book.accent }}
                      />
                      <span className="text-[color:var(--text-muted)]">{h}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex flex-wrap items-center gap-3">
                  <ShowcaseChips book={book} />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ─── Coming-soon grid ────────────────────────────────────── */}
      <section
        className="border-t border-[color:var(--border)]"
        data-testid="showcase-coming-soon"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § 02 — In production
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
              More books, <em className="text-[color:var(--primary)]">shipping soon.</em>
            </h2>
            <p className="mt-4 text-[color:var(--text-muted)] leading-relaxed">
              Authors from our launch cohort finishing their final passes.
              Full showcase entries live here as each book compiles.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMING_SOON.map((b, idx) => (
              <div
                key={b.title}
                className="bg-white border border-[color:var(--border)] p-6"
                data-testid={`showcase-upcoming-${idx}`}
              >
                <div
                  className="aspect-[3/4] rounded-md mb-5 p-6 flex flex-col justify-between"
                  style={{ background: b.coverGradient }}
                >
                  <div
                    className="text-[9px] uppercase tracking-[0.3em]"
                    style={{ color: b.accent }}
                  >
                    Coming soon
                  </div>
                  <div
                    className="font-editorial text-2xl font-bold leading-tight text-[#1a1a1a]"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {b.title}
                  </div>
                </div>
                <div className="text-xs uppercase tracking-[0.15em] text-[color:var(--text-muted)]">
                  {b.tag}
                </div>
                <div className="text-sm mt-1">{b.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA ────────────────────────────────────────── */}
      <section
        className="border-t border-[color:var(--border)] bg-[#f4f2ec]"
        data-testid="showcase-bottom-cta"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">
          <BookOpen
            className="w-10 h-10 mx-auto mb-6 text-[color:var(--primary)]"
            strokeWidth={1.25}
          />
          <h2 className="font-editorial text-4xl sm:text-5xl leading-tight">
            Your book, on this page next.
          </h2>
          <p className="mt-6 text-lg max-w-2xl mx-auto text-[color:var(--text-muted)]">
            Drop your manuscript into BookForge360 and we'll compile it in a
            single pass. Ship your book, get featured, inspire the next author.
          </p>
          <div className="mt-10">
            <Link
              to="/register"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="showcase-cta"
            >
              Start with a free upload{" "}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

// iter-62 — Interactive toggle chip cluster on each Showcase entry.
// Three modes: raw manuscript (original .docx download), interactive
// EPUB-style HTML preview (embedded PDF viewer served from /reformatted),
// and print-ready PDF download. Active chip is highlighted; the preview
// panel below swaps content without a page reload.
function ShowcaseChips({ book }) {
  const [mode, setMode] = useState("pdf");
  const CHIPS = [
    { key: "raw",  icon: FileText, label: "View Raw Document" },
    { key: "epub", icon: Eye,      label: "Preview Interactive Layout" },
    { key: "pdf",  icon: FileDown, label: "Download Print-Ready PDF" },
  ];
  return (
    <div className="w-full" data-testid={`showcase-chips-${book.id}`}>
      <div className="flex flex-wrap gap-2 mb-4">
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setMode(c.key)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-all duration-200 ${
              mode === c.key
                ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]"
                : "bg-white text-[color:var(--text)] border-[color:var(--border)] hover:border-[color:var(--text)]"
            }`}
            data-testid={`showcase-chip-${book.id}-${c.key}`}
          >
            <c.icon className="w-4 h-4" strokeWidth={1.5} />
            {c.label}
          </button>
        ))}
      </div>
      {mode === "raw" && (
        <div
          className="border border-[color:var(--border)] bg-[#faf9f6] p-6 rounded-md"
          data-testid={`showcase-panel-raw-${book.id}`}
        >
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-3">
            Raw manuscript · unformatted text blocks
          </div>
          <p className="text-sm text-[color:var(--text-muted)] leading-relaxed mb-4">
            The original Word file the author uploaded. No typography, no
            page breaks, no callout containers — just raw paragraphs.
          </p>
          <a
            href={book.before}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-2"
            data-testid={`showcase-raw-download-${book.id}`}
          >
            <Download className="w-4 h-4" strokeWidth={1.5} />
            Download original .docx
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
          </a>
        </div>
      )}
      {mode === "epub" && (
        <div
          className="border border-[color:var(--border)] bg-white rounded-md overflow-hidden"
          data-testid={`showcase-panel-epub-${book.id}`}
        >
          <div className="px-6 py-3 border-b border-[color:var(--border)] flex items-center justify-between text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
            <span>Interactive layout preview · Crimson Pro / Georgia serif</span>
            <a
              href={book.afterPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline inline-flex items-center gap-1 normal-case tracking-normal"
            >
              Open in new tab <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
            </a>
          </div>
          <iframe
            src={book.afterPdf}
            title={`${book.title} — interactive preview`}
            className="w-full h-[560px]"
            loading="lazy"
          />
        </div>
      )}
      {mode === "pdf" && (
        <div
          className="border border-[color:var(--border)] bg-[#faf9f6] p-6 rounded-md"
          data-testid={`showcase-panel-pdf-${book.id}`}
        >
          <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-3">
            Print-ready PDF · fully typeset
          </div>
          <p className="text-sm text-[color:var(--text-muted)] leading-relaxed mb-4">
            Compiled through <code>formatter_v2</code> with the Classic
            Trade style pack: 24pt bold chapter titles, 16pt bold sections,
            11pt Crimson Pro / Georgia body at 1.4 line-height, and full
            widow/orphan protection. Ready for KDP or IngramSpark upload.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={book.afterPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
              data-testid={`showcase-pdf-download-${book.id}`}
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download PDF
            </a>
            <a
              href={book.afterDocx}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost inline-flex items-center gap-2"
              data-testid={`showcase-docx-download-${book.id}`}
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download DOCX
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
